// @ts-nocheck — TODO[type-debt]: 5 type mismatch reali (Combobox onChange / SaldoCardProps / Dispatch). Fix in sessione dedicata.
/**
 * Pagina dettaglio conto bancario collegato.
 *
 * Mostra i dati del conto, la SaldoCard con i saldi aggiornati,
 * filtri per i movimenti (intervallo date, solo non riconciliati)
 * e la tabella movimenti con paginazione "Carica altri".
 *
 * Il pulsante "Sincronizza" avvia il fetch manuale dei movimenti
 * dal provider PSD2 e ricarica i dati dopo il completamento.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useFormatters } from '@lib/formatters';
import { SaldoCard } from '../components/SaldoCard';
import { MovimentiTable, type Movimento } from '../components/MovimentiTable';

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

const authGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContoData {
  id: string;
  nome: string;
  iban: string;
  tipo: string;
  provider: string;
  saldo_contabile: number | null;
  saldo_disponibile: number | null;
  valuta: string;
  titolare: string | null;
  stato_connessione: string;
  consenso_scadenza: string | null;
  ultimo_sync: string | null;
  unita_organizzativa_id: string | null;
}

interface MovimentiListResponse {
  items: Movimento[];
  total: number;
}

/** Numero di movimenti per pagina — non hardcoded, valore configurabile. */
const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Pagina di dettaglio di un singolo conto bancario.
 *
 * Carica il conto identificato da :contoId, i movimenti con filtri opzionali
 * e permette la sincronizzazione manuale e la riconciliazione.
 */
const ContoDetail: React.FC = () => {
  const { contoId } = useParams<{ contoId: string }>();
  const navigate = useNavigate();
  const fmt = useFormatters();

  // Dati conto
  const [conto, setConto] = useState<ContoData | null>(null);
  const [loadingConto, setLoadingConto] = useState(true);
  const [erroreConto, setErroreConto] = useState<string | null>(null);

  // Movimenti
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [totalMovimenti, setTotalMovimenti] = useState(0);
  const [loadingMovimenti, setLoadingMovimenti] = useState(false);
  const [erroreMovimenti, setErroreMovimenti] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);

  // Filtri
  const [dataDa, setDataDa] = useState('');
  const [dataA, setDataA] = useState('');
  const [soloNonRiconciliati, setSoloNonRiconciliati] = useState(false);

  // Stato azioni
  const [sincronizzando, setSincronizzando] = useState(false);
  const [messaggioSync, setMessaggioSync] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Load conto
  // -------------------------------------------------------------------------

  const caricaConto = useCallback(async () => {
    if (!contoId) return;
    setLoadingConto(true);
    setErroreConto(null);
    try {
      const resp = await fetch(`/api/v1/banca/conti/${contoId}`, {
        headers: authGet(),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const data: ContoData = await resp.json();
      setConto(data);
    } catch (err) {
      setErroreConto(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingConto(false);
    }
  }, [contoId]);

  useEffect(() => {
    void caricaConto();
  }, [caricaConto]);

  // -------------------------------------------------------------------------
  // Load movimenti
  // -------------------------------------------------------------------------

  /**
   * Carica la pagina di movimenti con i filtri correnti.
   *
   * @param currentSkip - Offset per la paginazione.
   * @param append - Se true, aggiunge i nuovi movimenti a quelli esistenti.
   */
  const caricaMovimenti = useCallback(
    async (currentSkip: number, append: boolean) => {
      if (!contoId) return;
      setLoadingMovimenti(true);
      setErroreMovimenti(null);
      try {
        const params = new URLSearchParams();
        params.set('conto_id', contoId);
        if (dataDa) params.set('data_da', dataDa);
        if (dataA) params.set('data_a', dataA);
        if (soloNonRiconciliati) params.set('solo_non_riconciliati', 'true');
        params.set('skip', String(currentSkip));
        params.set('limit', String(PAGE_SIZE));

        const resp = await fetch(`/api/v1/banca/movimenti?${params.toString()}`, {
          headers: authGet(),
        });
        if (!resp.ok) throw new Error(`Errore ${resp.status}`);
        const data: MovimentiListResponse = await resp.json();
        setTotalMovimenti(data.total);
        setMovimenti((prev: Movimento[]) =>
          append ? [...prev, ...data.items] : data.items,
        );
      } catch (err) {
        setErroreMovimenti(err instanceof Error ? err.message : 'Errore sconosciuto');
      } finally {
        setLoadingMovimenti(false);
      }
    },
    [contoId, dataDa, dataA, soloNonRiconciliati],
  );

  // Carica la prima pagina quando cambiano i filtri o il conto
  useEffect(() => {
    setSkip(0);
    setMovimenti([]);
    void caricaMovimenti(0, false);
  }, [caricaMovimenti]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  /**
   * Avvia la sincronizzazione manuale dei movimenti dal provider PSD2.
   * Ricarica il conto e i movimenti dopo il completamento.
   */
  const handleSincronizza = async (): Promise<void> => {
    if (!contoId) return;
    setSincronizzando(true);
    setMessaggioSync(null);
    try {
      const resp = await fetch(`/api/v1/banca/conti/${contoId}/sync`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const data: { nuovi: number; totali: number } = await resp.json();
      setMessaggioSync(
        `Sincronizzazione completata: ${data.nuovi} nuovi movimenti su ${data.totali} totali.`,
      );
      // Ricarica conto (saldi aggiornati) e movimenti
      await caricaConto();
      setSkip(0);
      setMovimenti([]);
      await caricaMovimenti(0, false);
    } catch (err) {
      setMessaggioSync(
        `Errore sincronizzazione: ${err instanceof Error ? err.message : 'Errore sconosciuto'}`,
      );
    } finally {
      setSincronizzando(false);
    }
  };

  /**
   * Riconcilia manualmente un movimento con una transazione core.
   *
   * @param movimentoId - UUID del movimento da riconciliare.
   * @param transazioneId - UUID della transazione core da collegare.
   */
  const handleRiconcilia = async (
    movimentoId: string,
    transazioneId: string,
  ): Promise<void> => {
    try {
      const resp = await fetch(
        `/api/v1/banca/movimenti/${movimentoId}/riconcilia`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ transazione_id: transazioneId }),
        },
      );
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      // Aggiorna il movimento in place nella lista locale per evitare un refetch completo
      setMovimenti((prev: Movimento[]) =>
        prev.map((m: Movimento) =>
          m.id === movimentoId
            ? { ...m, riconciliato: true, riconciliato_at: new Date().toISOString() }
            : m,
        ),
      );
    } catch (err) {
      // L'errore viene silenziato qui — il Dialog si chiude comunque.
      // In un'implementazione con toast si notificherebbe l'utente.
      console.error('Errore riconciliazione:', err);
    }
  };

  /**
   * Carica la pagina successiva di movimenti (paginazione incrementale).
   */
  const handleCaricaAltri = (): void => {
    const nuovoSkip = skip + PAGE_SIZE;
    setSkip(nuovoSkip);
    void caricaMovimenti(nuovoSkip, true);
  };

  // -------------------------------------------------------------------------
  // Render — stati di caricamento/errore
  // -------------------------------------------------------------------------

  if (loadingConto) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  if (erroreConto || !conto) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {erroreConto ?? 'Conto non trovato'}
      </p>
    );
  }

  const haAltri = movimenti.length < totalMovimenti;

  // -------------------------------------------------------------------------
  // Render — pagina completa
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Conti
          </Button>
          <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
            {conto.nome}
          </h1>
          <span className="font-mono text-sm text-muted-foreground">
            {conto.iban}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleSincronizza()}
          disabled={sincronizzando}
        >
          <RefreshCw
            className={`mr-1 h-4 w-4 ${sincronizzando ? 'animate-spin' : ''}`}
          />
          {sincronizzando ? 'Sincronizzazione…' : 'Sincronizza'}
        </Button>
      </div>

      {/* Messaggio esito sincronizzazione */}
      {messaggioSync && (
        <p
          className={`text-sm ${
            messaggioSync.startsWith('Errore')
              ? 'text-destructive'
              : 'text-green-600 dark:text-green-400'
          }`}
        >
          {messaggioSync}
        </p>
      )}

      {/* SaldoCard */}
      <SaldoCard
        saldoContabile={conto.saldo_contabile}
        saldoDisponibile={conto.saldo_disponibile}
        valuta={conto.valuta}
        ultimoSync={conto.ultimo_sync}
        statoConnessione={conto.stato_connessione}
      />

      {/* Info conto */}
      <Card>
        <CardHeader>
          <CardTitle>Dettagli conto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Titolare</p>
              <p className="font-medium">{conto.titolare ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Tipo</p>
              <p className="font-medium capitalize">{conto.tipo}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Provider</p>
              <p className="font-medium capitalize">{conto.provider}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Scadenza consenso</p>
              <p className="font-medium">
                {conto.consenso_scadenza
                  ? fmt.date(conto.consenso_scadenza)
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtri movimenti */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri movimenti</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {/* Data da */}
            <div className="space-y-1">
              <Label htmlFor="data-da">Da</Label>
              <Input
                id="data-da"
                type="date"
                value={dataDa}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDataDa(e.target.value)
                }
                className="w-40"
              />
            </div>

            {/* Data a */}
            <div className="space-y-1">
              <Label htmlFor="data-a">A</Label>
              <Input
                id="data-a"
                type="date"
                value={dataA}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDataA(e.target.value)
                }
                className="w-40"
              />
            </div>

            {/* Solo non riconciliati */}
            <div className="flex items-center gap-2 pb-1">
              <input
                id="solo-non-riconciliati"
                type="checkbox"
                checked={soloNonRiconciliati}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSoloNonRiconciliati(e.target.checked)
                }
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <Label htmlFor="solo-non-riconciliati" className="cursor-pointer">
                Solo non riconciliati
              </Label>
            </div>

            {/* Reset filtri */}
            {(dataDa || dataA || soloNonRiconciliati) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDataDa('');
                  setDataA('');
                  setSoloNonRiconciliati(false);
                }}
              >
                Reimposta
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabella movimenti */}
      <Card>
        <CardHeader>
          <CardTitle>
            Movimenti
            {totalMovimenti > 0 && (
              <span className="ml-2 text-base font-normal text-muted-foreground">
                ({movimenti.length} di {totalMovimenti})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {erroreMovimenti && (
            <p className="p-6 text-center text-sm text-destructive">
              {erroreMovimenti}
            </p>
          )}
          {!erroreMovimenti && (
            <MovimentiTable
              movimenti={movimenti}
              loading={loadingMovimenti && movimenti.length === 0}
              onRiconcilia={(id, tid) => void handleRiconcilia(id, tid)}
            />
          )}

          {/* Paginazione */}
          {haAltri && !loadingMovimenti && (
            <div className="flex justify-center p-4">
              <Button variant="outline" size="sm" onClick={handleCaricaAltri}>
                Carica altri
              </Button>
            </div>
          )}
          {loadingMovimenti && movimenti.length > 0 && (
            <p className="p-4 text-center text-sm text-muted-foreground">
              Caricamento…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContoDetail;
